import { yupResolver } from '@hookform/resolvers/yup';
import { InputLabel } from '@mui/material';
import { useForm } from 'react-hook-form';
import { Text, Icons, Box, Select, Button, Input, Grid } from '@/theme/index';
import { schemaMileageForm } from '@/utils/schemaForms/schemaForms';
import { useStatesOptionsAbbr } from '@/utils/states';
import { StyledBox } from './components';
import { useVehicleValuation } from '../hooks/useVehicleValuation';

/**
 * The shape of the default inputs for the VehicleValuationFilter component.
 */
export type DefaultInputs = {
  mileage: number;
  region: string;
  vin: string;
  plate: string;
  state: string;
};

/**
 * Props for the VehicleValuationFilter component.
 */
interface VehicleValuationFilterProps {
  /**
   * The default inputs used to prefill the form fields.
   */
  defaultInputs: DefaultInputs;
}

/**
 * A component that displays a form to filter vehicle valuation data.
 * @param {VehicleValuationFilterProps} props - The props for the component.
 * @returns {JSX.Element} - The JSX element representing the VehicleValuationFilter component.
 */

export function VehicleValuationFilter({
  defaultInputs,
}: VehicleValuationFilterProps) {
  const { updateValuation } = useVehicleValuation({});
  const { register, handleSubmit } = useForm({
    defaultValues: defaultInputs,
    resolver: yupResolver(schemaMileageForm),
  });
  const states = useStatesOptionsAbbr();
  states.shift();

  return (
    <StyledBox
      component="form"
      onSubmit={handleSubmit((data) => {
        const newValuationData = {
          vin: data?.vin,
          plate: data?.plate,
          state: data?.state,
          mileage: data.mileage,
          region: data.region,
        };

        updateValuation.mutate(newValuationData);
      })}
    >
      <Box display="flex" marginBottom={2}>
        <Icons.Tune
          fontSize="small"
          sx={(theme) => ({ marginRight: theme.spacing(1) })}
        />
        <Text sx={{ fontWeight: 'bold' }}>
          Add filters to see market values specific to your vehicle
        </Text>
      </Box>
      <Grid container spacing={2} alignItems="flex-end">
        <Grid item xs={7} md={4} display="flex" flexDirection="column">
          <InputLabel htmlFor="mileage-input">Milage</InputLabel>
          <Input
            id="mileage-input"
            type="number"
            {...register('mileage', { required: true, min: 0, max: 1000000 })}
          />
        </Grid>
        <Grid item xs={5} md={4} display="flex" flexDirection="column">
          <InputLabel id="helper-label-state">State</InputLabel>
          <Select
            labelId="helper-label-state"
            label="State"
            id="state"
            defaultValue={defaultInputs.region}
            options={states}
            {...register('region')}
          />
        </Grid>
        <Grid item xs={12} md={4}>
          <Button
            sx={(theme) => ({
              width: '100%',
              marginBottom: theme.spacing(0.5),
              [theme.breakpoints.up('md')]: {
                width: 'fit-content',
              },
            })}
            type="submit"
            variant="contained"
            color="success"
          >
            Update values
          </Button>
        </Grid>
      </Grid>
    </StyledBox>
  );
}
